import React, { useLayoutEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ArrowRight, Cpu, Cloud, Globe } from 'lucide-react';
import './Home.css';

gsap.registerPlugin(ScrollTrigger);

const featuresData = [
    {
        id: 1,
        icon: <Cpu size={32} />,
        title: "AI-Powered Solutions",
        description: "Leverage AI to optimize business processes and drive efficiency."
    },
    {
        id: 2,
        icon: <Cloud size={32} />,
        title: "Scalable Cloud Computing",
        description: "Embrace cloud infrastructure with real-time data processing."
    },
    {
        id: 3,
        icon: <Globe size={32} />,
        title: "Advanced Research",
        description: "Explore cutting-edge AI and ML research shaping the future."
    }
];

const Home = () => {
    const containerRef = useRef(null);
    const heroTextRef = useRef(null);
    const featuresRef = useRef(null);

    useLayoutEffect(() => {
        const ctx = gsap.context(() => {
            // Hero Animation
            const tl = gsap.timeline();

            tl.from("h1 span", {
                y: 100,
                opacity: 0,
                duration: 1.5,
                stagger: 0.1,
                ease: "power4.out"
            })
                .from(".hero-subtitle", {
                    y: 20,
                    opacity: 0,
                    duration: 1,
                    ease: "power2.out"
                }, "-=1")
                .from(".cta-button", {
                    y: 20,
                    opacity: 0,
                    duration: 1,
                    ease: "power2.out"
                }, "-=0.8");

            // Features Scroll Animation
            gsap.fromTo(".feature-card",
                { y: 50, opacity: 0 },
                {
                    y: 0,
                    opacity: 1,
                    duration: 0.8,
                    stagger: 0.2,
                    scrollTrigger: {
                        trigger: ".features-grid",
                        start: "top 80%",
                    }
                }
            );

            // Innovation Scroll Animation
            gsap.fromTo(".innovation-section",
                { y: 50, opacity: 0 },
                {
                    y: 0,
                    opacity: 1,
                    duration: 1,
                    scrollTrigger: {
                        trigger: ".innovation-section",
                        start: "top 85%",
                    }
                }
            );

        }, containerRef);

        return () => ctx.revert();
    }, []);

    return (
        <div className="home-container" ref={containerRef}>
            {/* Hero Section */}
            <section className="hero-section">
                <div className="hero-content">
                    <h1 ref={heroTextRef}>
                        <span className="block">Revolutionizing</span>
                        <span className="block text-gradient">AI & Cloud Technology</span>
                    </h1>
                    <p className="hero-subtitle">
                        Innovate with the latest advancements in Artificial Intelligence and Cloud Computing.
                    </p>
                    <Link to="/contactus" className="cta-button glass-panel">
                        Get in Touch <ArrowRight size={18} />
                    </Link>
                </div>
            </section>

            {/* Features Section */}
            <section className="features-section">
                <div className="container">
                    <div className="features-grid" ref={featuresRef}>
                        {featuresData.map((feature) => (
                            <div className="feature-card glass-panel" key={feature.id}>
                                <div className="icon-wrapper">{feature.icon}</div>
                                <h3>{feature.title}</h3>
                                <p>{feature.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Innovation Section */}
            <section className="innovation-section glass-panel">
                <div className="container">
                    <h2>Innovation at its Core</h2>
                    <p>The convergence of AI and Cloud Technology is revolutionizing industries worldwide.</p>
                </div>
            </section>

            {/* Footer */}
            <footer className="home-footer">
                <p>&copy; 2024 Galilee Research - AI & Cloud Innovation</p>
            </footer>
        </div>
    );
};

export default Home;
